import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { api } from "../lib/api";
import { formatDate } from "../lib/format";

const STATUSES = [
  { v: "planning", label: "In pianificazione" },
  { v: "active", label: "Attivo" },
  { v: "on_hold", label: "In pausa" },
  { v: "done", label: "Completato" },
  { v: "archived", label: "Archiviato" },
];

const emptyForm = { name: "", description: "", area_id: null, sub_area_id: null, status: "planning", start_date: "", due_date: "" };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const loadAll = async () => {
    try {
      const [p, a] = await Promise.all([
        api.listProjects(statusFilter ? { status: statusFilter } : {}),
        api.listAreas(),
      ]);
      setProjects(p); setAreas(a);
    } catch { toast.error("Errore"); }
  };
  useEffect(() => { loadAll(); }, [statusFilter]);

  const submit = async (e) => {
    e.preventDefault();
    const p = { ...form,
      start_date: form.start_date || null,
      due_date: form.due_date || null,
      area_id: form.area_id || null,
      sub_area_id: form.sub_area_id || null,
    };
    try {
      if (editing) { await api.updateProject(editing.id, p); toast.success("Progetto aggiornato"); }
      else { await api.createProject(p); toast.success("Progetto creato"); }
      setForm(emptyForm); setEditing(null); setShowForm(false); await loadAll();
    } catch { toast.error("Errore"); }
  };
  const remove = async (id) => {
    if (!confirm("Eliminare questo progetto?")) return;
    try { await api.deleteProject(id); await loadAll(); toast.success("Eliminato"); }
    catch { toast.error("Errore"); }
  };
  const startEdit = (p) => {
    setEditing(p); setShowForm(true);
    setForm({
      name: p.name, description: p.description || "", area_id: p.area_id, sub_area_id: p.sub_area_id,
      status: p.status, start_date: p.start_date || "", due_date: p.due_date || "",
    });
  };
  const areaName = (id) => areas.find((a) => a.id === id)?.name || "—";
  const areaColor = (id) => areas.find((a) => a.id === id)?.color || "#B8B8B8";
  const subAreas = form.area_id ? (areas.find((a) => a.id === form.area_id)?.sub_areas || []) : [];

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Struttura</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">Progetti</h1>
          <p className="mt-2 text-sm opacity-80 max-w-2xl">Contenitori di lavoro collegati a un'area o sotto-area.</p>
        </div>
        <button className="pxos-btn-primary text-sm" onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm); }} data-testid="add-project-btn"><Plus size={14}/> Nuovo progetto</button>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-widest opacity-70">Filtro stato</span>
        <select className="pxos-input w-56" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="project-status-filter">
          <option value="">Tutti</option>
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
      </div>

      {showForm && (
        <form onSubmit={submit} className="pxos-card p-4 space-y-3" data-testid="project-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block sm:col-span-2"><span className="text-xs uppercase tracking-widest opacity-70">Nome</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pxos-input" data-testid="project-name" /></label>
            <label className="block sm:col-span-2"><span className="text-xs uppercase tracking-widest opacity-70">Descrizione</span>
              <textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="pxos-input" data-testid="project-description" /></label>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Area</span>
              <select value={form.area_id || ""} onChange={(e) => setForm({ ...form, area_id: e.target.value || null, sub_area_id: null })} className="pxos-input" data-testid="project-area">
                <option value="">—</option>
                {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select></label>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Sotto-area</span>
              <select value={form.sub_area_id || ""} onChange={(e) => setForm({ ...form, sub_area_id: e.target.value || null })} className="pxos-input" data-testid="project-sub-area" disabled={!form.area_id}>
                <option value="">—</option>
                {subAreas.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></label>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Stato</span>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="pxos-input" data-testid="project-status">
                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select></label>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Inizio</span>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="pxos-input" data-testid="project-start" /></label>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Scadenza</span>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="pxos-input" data-testid="project-due" /></label>
          </div>
          <div className="flex gap-2">
            <button className="pxos-btn-primary text-sm" data-testid="project-submit"><Check size={14}/> {editing ? "Aggiorna" : "Crea"}</button>
            <button type="button" className="text-sm underline opacity-70" onClick={() => { setShowForm(false); setEditing(null); }} data-testid="project-cancel">Annulla</button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {projects.map((p) => (
          <div key={p.id} className="pxos-card p-4 space-y-2" data-testid="project-card">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs flex items-center gap-1.5 opacity-70">
                  <span className="w-2 h-2 rounded-full" style={{ background: areaColor(p.area_id) }}/>
                  <span data-testid="project-area-name">{areaName(p.area_id)}</span>
                </div>
                <div className="font-medium truncate" data-testid="project-name-display">{p.name}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(p)} className="p-1 hover:bg-black/5 rounded" data-testid="project-edit-btn"><Pencil size={14}/></button>
                <button onClick={() => remove(p.id)} className="p-1 hover:bg-black/5 rounded text-red-600" data-testid="project-delete-btn"><Trash2 size={14}/></button>
              </div>
            </div>
            {p.description && <div className="text-xs opacity-70 line-clamp-2">{p.description}</div>}
            <div className="flex items-center gap-2 text-xs opacity-70">
              <span className="pxos-badge">{STATUSES.find((s) => s.v === p.status)?.label || p.status}</span>
              {p.due_date && <span>{formatDate(p.due_date)}</span>}
            </div>
          </div>
        ))}
        {projects.length === 0 && <div className="col-span-full pxos-card p-8 text-center opacity-70">Nessun progetto ancora.</div>}
      </div>
    </div>
  );
}
